import * as React from "react";
import { cn } from "@/lib/cn";

/* ─── Context ─── */
interface DDCtxValue {
  open: boolean;
  setOpen: (v: boolean) => void;
}

const DDCtx = React.createContext<DDCtxValue | null>(null);
function useDDCtx() {
  const ctx = React.useContext(DDCtx);
  if (!ctx) throw new Error("DropdownMenu components must be within <DropdownMenu>");
  return ctx;
}

/* ─── Root ─── */
export function DropdownMenu({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  return (
    <DDCtx.Provider value={{ open, setOpen }}>
      <div className="relative inline-block">{children}</div>
    </DDCtx.Provider>
  );
}

/* ─── Trigger ─── */
export function DropdownMenuTrigger({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { open, setOpen } = useDDCtx();
  return (
    <button
      type="button"
      className={className}
      aria-expanded={open}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(!open);
      }}
    >
      {children}
    </button>
  );
}

/* ─── Content ─── */
export function DropdownMenuContent({
  children,
  className,
  align = "start",
}: {
  children: React.ReactNode;
  className?: string;
  align?: "start" | "end";
}) {
  const { open } = useDDCtx();
  if (!open) return null;

  return (
    <div
      className={cn(
        "absolute z-50 mt-1 min-w-[160px] rounded-md border border-border bg-bg-elevated py-1 shadow-lg",
        align === "end" ? "right-0" : "left-0",
        className,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

/* ─── Item ─── */
export function DropdownMenuItem({
  children,
  className,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const { setOpen } = useDDCtx();
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        "w-full px-3 py-1.5 text-left text-sm text-fg transition-colors",
        "hover:bg-bg-sunken focus-visible:bg-bg-sunken focus-visible:outline-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className,
      )}
      onClick={() => {
        onClick?.();
        setOpen(false);
      }}
    >
      {children}
    </button>
  );
}

/* ─── Separator ─── */
export function DropdownMenuSeparator({ className }: { className?: string }) {
  return <hr className={cn("my-1 border-border", className)} />;
}
